import { useMutation } from '@tanstack/react-query';

import { updateEmailFileStatus } from '@/src/components/project/Inbox2/api/updateEmailFileStatus';
import { EmailDeliveryType } from '@/src/components/project/Inbox2/types';
import { MutationConfig, queryClient } from '@/src/lib/react-query/react-query';

import { emailFileActivityLogsKeys } from './getEmailFileActivityLogs';
import { emailFileDetailsKeys } from './getEmailFileDetails';

import { emailEntriesKeys } from '../../../api/getEmailEntries';
import { emailEntryKeys } from '../../../api/getEmailEntry';
import { emailThreadAttachmentsKeys } from '../../../api/getEmailThreadAttachments';

type useUpdateStatusOptions = {
  inboxEntryId;
  emailId;
  fileId;
  projectId;
  config?: MutationConfig<typeof updateEmailFileStatus>;
};

export const useUpdateEmailFileStatus = ({
  inboxEntryId,
  emailId,
  fileId,
  projectId,
  config,
}: useUpdateStatusOptions) => {
  return useMutation({
    onSuccess: (data) => {
      queryClient.setQueryData(
        [...emailFileDetailsKeys.detail({ projectId, emailId, inboxEntryId, fileId })],
        (prev: any) => {
          return {
            ...prev,
            data: data.data['inbox-email-attachment'],
          };
        }
      );

      queryClient.invalidateQueries([
        ...emailFileActivityLogsKeys.detail({ projectId, fileId, emailId, inboxEntryId }),
      ]);

      // Sync with email entry api detail containing attachments
      // @TODO: use allowed-status-changes from inboxDocument due to different values
      if (
        queryClient.getQueryData([...emailEntryKeys.detail({ emailId, inboxEntryId, projectId })])
      ) {
        queryClient.setQueryData(
          [...emailEntryKeys.detail({ emailId, inboxEntryId, projectId })],
          (prev: any) => {
            if (!prev) {
              return prev;
            }

            return {
              ...prev,
              data: {
                ...data.data['inbox-email'],
                attachments: data.data['inbox-email'].attachments.map((attachmentItem) => {
                  if (attachmentItem.id === fileId) {
                    return data.data['inbox-email-attachment'];
                  }

                  return attachmentItem;
                }),
              },
            };
          }
        );
      }

      // Sync with email entries list
      queryClient.setQueriesData(
        [...emailEntriesKeys.lists({ projectId, inboxEntryId })],
        (prev: any) => {
          if (!prev) {
            return prev;
          }

          return {
            ...prev,
            pages: prev.pages.map((page) => {
              return {
                ...page,
                data: {
                  ...page.data,
                  emails: page.data.emails.map((emailEntry) => {
                    if (emailEntry.id === emailId) {
                      return {
                        ...data.data['inbox-email'],
                        attachments: data.data['inbox-email'].attachments.map((attachmentItem) => {
                          if (attachmentItem.id === fileId) {
                            return {
                              ...data.data['inbox-email-attachment'],
                              ...attachmentItem,
                            };
                          }

                          return attachmentItem;
                        }),
                      };
                    }
                    return emailEntry;
                  }),
                },
              };
            }),
          };
        }
      );

      const emailThreadAttachmentUpdateData = {
        status: data.data['inbox-email-attachment'].status,
        read: data.data['inbox-email-attachment'].read,
        'allowed-status-changes': data.data['inbox-email-attachment']['allowed-status-changes'],
        'change-status': data.data['inbox-email-attachment']['change-status'],
      };

      // Sync with email thread attachments list
      queryClient.setQueriesData(
        [
          ...emailThreadAttachmentsKeys.lists({
            inboxEntryId,
            projectId,
            type: EmailDeliveryType.inbound,
          }),
        ],
        (prev: any) => {
          if (!prev) {
            return prev;
          }

          return {
            ...prev,
            pages: prev.pages.map((page) => {
              return {
                ...page,
                data: {
                  ...page.data,
                  attachments: page.data.attachments.map((attachmentItem) => {
                    if (attachmentItem.id === fileId) {
                      return {
                        ...attachmentItem,
                        ...emailThreadAttachmentUpdateData,
                      };
                    }
                    return attachmentItem;
                  }),
                },
              };
            }),
          };
        }
      );

      queryClient.setQueriesData(
        [
          ...emailThreadAttachmentsKeys.lists({
            inboxEntryId,
            projectId,
            type: EmailDeliveryType.outbound,
          }),
        ],
        (prev: any) => {
          if (!prev) {
            return prev;
          }

          return {
            ...prev,
            pages: prev.pages.map((page) => {
              return {
                ...page,
                data: {
                  ...page.data,
                  attachments: page.data.attachments.map((attachmentItem) => {
                    if (attachmentItem.id === fileId) {
                      return {
                        ...attachmentItem,
                        ...emailThreadAttachmentUpdateData,
                      };
                    }
                    return attachmentItem;
                  }),
                },
              };
            }),
          };
        }
      );
    },
    ...config,
    mutationFn: ({ status }) =>
      updateEmailFileStatus({ inboxEntryId, emailId, projectId, fileId, status }),
  });
};
